// @ts-check
import { ControlOutlined, DeleteOutlined, EditOutlined, PieChartOutlined, UserAddOutlined } from "@ant-design/icons";
import { Dropdown, Popover } from "antd";
import { update_schedule } from '~/api/zero-api';
import RespondersPopover from '~/components/Shared/RespondersPopover';
import UserPopoverCard from '~/components/Shared/UserPopoverCard';
import { useZeroContext } from '~/components/ZeroContext';
import { useCurrentUser } from '~/hooks/reduxHooks';
import useOrganizationId from '~/hooks/useOrganizationId';
import useZeroSelector from '~/hooks/useZeroSelector';
import { isAdmin, safeProfilePic, updateHistoryState } from '~/other/Helper';
import NotificationAlert from '~/other/NotificationAlert';
import { useMemo } from "react";
import { IoIosMore as MoreIcon } from "react-icons/io";
import { Link, useHistory } from "react-router-dom";
import { useScheduleDataGridContext } from '~/components/Forms/ScheduleDataGrid/ScheduleDataGrid.context';
import { EventNames, getScheduleAnalyticsLink } from '~/components/Forms/ScheduleDataGrid/ScheduleDataGrid.utils';

/**
 * @param {AgGridReactLib.CustomCellRendererProps} props
 */
export function ScheduleActionCell({data: schedule, api}) {
    const { setActiveModal } = useScheduleDataGridContext();
    const user = useCurrentUser();
    const history = useHistory();
    const orgId = useOrganizationId();

    if (!schedule) {
        return null;
    }

    const userIsAdmin = isAdmin(user);
    const deleteIsDisabled = !userIsAdmin;

    const items = [
        {
            key: "edit",
            icon: <EditOutlined />,
            label: "Edit Schedule",
        },
        {
            key: "status",
            icon: <ControlOutlined />,
            label: "Change Status",
        },
        {
            key: "analytics",
            icon: <PieChartOutlined />,
            label: "View Analytics",
        },
        {
            type: "divider",
        },
        {
            key: "delete",
            label: "Archive Schedule",
            icon: <DeleteOutlined />,
            className: deleteIsDisabled ? "" : "zero-delete-red",
            disabled: deleteIsDisabled,
        },
    ];

    const handleMenuClick = ({ key }) => {
        if (key === "analytics") {
            updateHistoryState(history, {gridState: api.getState()});
            history.push(getScheduleAnalyticsLink(schedule, orgId));
        } else {
            setActiveModal({
                key,
                schedule,
            });
        }
    };

    const menu = {
        items,
        onClick: handleMenuClick,
    };

    return (
        <div className="more" style={{ marginTop: "6px", color: "grey", lineHeight: 0, height: "fit-content" }}>
            <Dropdown
                // @ts-ignore
                menu={menu}
                trigger={["click"]}
            >
                <button className="ButtonLink ant-dropdown-link">
                    <MoreIcon style={{ height: "20px", width: "20px" }} />
                </button>
            </Dropdown>
        </div>
    );
}

/**
 * @param {AgGridReactLib.CustomCellRendererProps} props
 */
export function ScheduleNameCell({data: schedule, value: name, api}) {
    const history = useHistory();
    const orgId = useOrganizationId();

    if (!schedule) {
        return null;
    }

    const scheduleLink = getScheduleAnalyticsLink(schedule, orgId);

    const onClick = () => {
        updateHistoryState(history, {gridState: api.getState()});
    }

    return (
        <div>
            <Link to={scheduleLink} className="blue-link" style={{ display: "block" }} onClick={onClick}>
                {name}
            </Link>
        </div>
    );
}

/**
 * @param {AgGridReactLib.CustomCellRendererProps} props
 */
export function ScheduleTypeCell({data: schedule, value}) {
    if (!schedule) {
        return null;
    }
    
    return (
        <span className="zero-dark-grey" style={{ fontWeight: "400" }}>
            {schedule.type === "regular" ? "Regular" : "Shared"}
        </span>
    );
}

/**
 * @param {AgGridReactLib.CustomCellRendererProps} props
 */
export function ScheduleTemplateCell({data: schedule}) {
    if (!schedule) {
        return null;
    }

    return (
        <span className="zero-dark-grey" style={{ fontWeight: "400" }}>
            {schedule.form.name}
        </span>
    );
}

/**
 * @param {AgGridReactLib.CustomCellRendererProps} props
 */
export function ScheduleTeamsCell({data: schedule}) {
    if (!schedule) {
        return null;
    }

    const teams = useMemo(
        () => schedule.teams.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase ? 1 : -1)),
        [schedule]
    );

    const content = useMemo(() => teams.map((team) => <div key={team.team_uuid}>{team.name}</div>), [teams]);

    return (
        <Popover content={content} placement="topLeft" overlayStyle={{ maxWidth: "220px" }}>
            <span className="zero-dark-grey" style={{ fontWeight: "400", display: "block" }}>
                {teams.length}
            </span>
        </Popover>
    );
}

/**
 * @param {AgGridReactLib.CustomCellRendererProps} props
 */
export function ScheduleRespondersCell({data: schedule}) {
    const { events } = useZeroContext();
    const teams = useZeroSelector((state) => state.teams_helper.teams);
    const user = useCurrentUser();

    if (!schedule) {
        return null;
    }

    const responders = schedule.subscribers;
    const responderCount = schedule.subscribers_count;

    const updateResponders = async (responderIds, assignedToTeam) => {
        let body = JSON.stringify({
            name: schedule.name,
            enabled: true,
            team_uuids: schedule.teams.map((team) => team.team_uuid),
            form_uuid: schedule.form.form_uuid,
            user_uuids: responderIds,
            assigned_to_team: assignedToTeam,
            repeat_period: schedule.repeat_period,
            start_date: schedule.start_date,
            custom_start_time: schedule.custom_start_time,
            custom_start_timezone: schedule.custom_start_timezone,
            end_date: schedule.end_date,
        });

        try {
            const res = await update_schedule(schedule.scheduler_uuid, body);
            const data = await res.json();

            NotificationAlert("success", "", "Schedule updated.");
            events.emit(EventNames.REFRESH_DATA);
        } catch (err) {
            NotificationAlert("error", "", "Unable to update schedule.");
        }
    };

    if (schedule.assigned_to_team) {
        return (
            <RespondersPopover schedule={schedule} selected_responders={responders} updateResponders={updateResponders}>
                <p className="zero-dark-grey text-center mar-btm-0 link-hover" style={{ fontWeight: "400" }}>
                    All Team Members
                </p>
            </RespondersPopover>
        );
    } else {
        return (
            <div style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                {responders.slice(0, 3).map((user, index) => (
                    <UserPopoverCard
                        key={index}
                        user={user}
                        showRemoveScheduleResponder
                        responders={responders}
                        schedule={schedule}
                        setSchedule={() => events.emit(EventNames.REFRESH_DATA)}
                    >
                        <span style={{ cursor: "pointer" }}>
                            {safeProfilePic(user, "img-sm img-circle bulletin", "bulletin", {
                                marginRight: "5px",
                                display: "inline-block",
                            })}
                        </span>
                    </UserPopoverCard>
                ))}
                {responders.length > 3 && (
                    <span>
                        <RespondersPopover
                            schedule={schedule}
                            selected_responders={responders}
                            updateResponders={updateResponders}
                        >
                            <div
                                className={"default-avatar-circle bulletin hover-cursor"}
                                style={{ marginRight: "5px", display: "inline-block", border: "1px solid #505050" }}
                            >
                                <p className="initials" style={{ color: "#505050" }}>
                                    +{responderCount - 3}
                                </p>
                            </div>
                        </RespondersPopover>
                    </span>
                )}
                {responders.length <= 3 && (
                    <RespondersPopover
                        schedule={schedule}
                        selected_responders={responders}
                        updateResponders={updateResponders}
                    >
                        <button
                            className="ButtonLink post-details-content mar-top-5"
                            style={{ verticalAlign: "sub", height: "26px", width: "26px" }}
                        >
                            <UserAddOutlined style={{ fontSize: "19px" }} />
                        </button>
                    </RespondersPopover>
                )}
            </div>
        );
    }
}

/**
 * @param {AgGridReactLib.CustomCellRendererProps} props
 */
export function ScheduleAssignmentsCell({data: schedule}) {
    if (!schedule) {
        return null;
    }

    return (
        <span className="zero-dark-grey" style={{ fontWeight: "400" }}>
            {schedule.assigns_count}
        </span>
    );
}

/**
 * @param {AgGridReactLib.CustomCellRendererProps} props
 */
export function ScheduleCompletedCell({data: schedule}) {
    if (!schedule) {
        return null;
    }

    return (
        <span className="zero-dark-grey" style={{ fontWeight: "400" }}>
            {schedule.assigns_completed}
        </span>
    );
}

/**
 * @param {AgGridReactLib.CustomCellRendererProps} props
 */
export function ScheduleStatusCell({data: schedule}) {
    if (!schedule) {
        return null;
    }

    const isActive = schedule.enabled;
    
    return (
        <div style={{ display: "flex", alignItems: "center"}}>
            <span className={`badge ${isActive ? 'badge-success' : 'badge-warning'} badge-icon`} />
            <span className="zero-dark-grey" style={{ fontWeight: "400", marginBottom: "2px" }}>
                {isActive ? "Active" : "Paused"}
            </span>
        </div>
    );
}

 